import React from 'react';
import PropTypes from 'prop-types';
import Skeleton from './Skeleton';
import styles from './Skeleton.module.scss';

/* ────────────────────────────────────────────────────────────
 * SkeletonRow — table-row-shaped skeleton.
 *
 * Renders `count` rows of `columns` cells. Cell widths vary
 * by index so the row reads as data rather than a flat grey
 * bar.
 *
 *   <SkeletonRow count={6} columns={5} />
 *
 * The DataTable scroll wrapper handles overflow; this just
 * lays out semantically-correct <tr>/<td> so it slots in
 * cleanly where a real <tbody> would render.
 * ──────────────────────────────────────────────────────────── */

// Pseudo-random per-cell width so each row looks like real data
// instead of a uniform grey bar. Deterministic on (rowIdx, colIdx)
// so the shimmer doesn't visibly re-shuffle on re-mount.
const cellWidthFor = (rowIdx, colIdx, columns) => {
  if (colIdx === 0) return '70%';
  if (colIdx === columns - 1) return '40%';
  const seeded = ((rowIdx * 7 + colIdx * 13) % 5) / 10;
  return `${50 + seeded * 40}%`;
};

const SkeletonRow = ({ count, columns, className }) => {
  return Array.from({ length: count }, (_, rowIdx) => (
    <tr key={rowIdx} className={[styles.row, className || ''].filter(Boolean).join(' ')}>
      {Array.from({ length: columns }, (_, colIdx) => (
        <td key={colIdx} className={styles.rowCell}>
          <Skeleton width={cellWidthFor(rowIdx, colIdx, columns)} height="0.9rem" />
        </td>
      ))}
    </tr>
  ));
};

SkeletonRow.propTypes = {
  count: PropTypes.number,
  columns: PropTypes.number.isRequired,
  className: PropTypes.string,
};

SkeletonRow.defaultProps = {
  count: 6,
  className: '',
};

export default SkeletonRow;
